const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const pad = (value) => String(value).padStart(2, "0");

export const toDateOnly = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const getMonthKeysInRange = (start, end) => {
  if (!DATE_ONLY_RE.test(start || "") || !DATE_ONLY_RE.test(end || "")) {
    return [];
  }

  const [startYear, startMonth] = start.split("-").map(Number);
  const [endYear, endMonth] = end.split("-").map(Number);
  const months = [];
  let cursor = new Date(startYear, startMonth - 1, 1);
  const last = new Date(endYear, endMonth - 1, 1);

  while (cursor <= last) {
    months.push(`${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  return months;
};

export const getPeriodRange = (period, customRange, now = new Date()) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === "weekly") {
    const mondayOffset = (today.getDay() + 6) % 7;
    const start = new Date(today);
    start.setDate(today.getDate() - mondayOffset);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start: toDateOnly(start), end: toDateOnly(end) };
  }

  if (period === "custom") {
    return {
      start: customRange?.start || "",
      end: customRange?.end || "",
    };
  }

  return {
    start: toDateOnly(new Date(today.getFullYear(), today.getMonth(), 1)),
    end: toDateOnly(today),
  };
};

export const isValidDateRange = ({ start, end }) =>
  DATE_ONLY_RE.test(start || "") && DATE_ONLY_RE.test(end || "") && start <= end;

const parsePublications = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getReportDateKey = (value) =>
  typeof value === "string" ? value.slice(0, 10) : "";

export const filterReportsByRange = (reports, range) =>
  (Array.isArray(reports) ? reports : []).filter((report) => {
    const reportDate = getReportDateKey(report?.data_servizio);
    return reportDate >= range.start && reportDate <= range.end;
  });

export const aggregateServiceReports = (reports) => {
  const publicationMap = new Map();
  const volunteerIds = new Set();
  const totals = {
    reportCount: 0,
    volunteerCount: 0,
    serviceMinutes: 0,
    oreLdc: 0,
    studi: 0,
    publications: 0,
    publicationBreakdown: [],
  };

  (Array.isArray(reports) ? reports : []).forEach((report) => {
    totals.reportCount += 1;
    if (report?.volontario_id != null) {
      volunteerIds.add(String(report.volontario_id));
    }
    totals.serviceMinutes += toNumber(report?.ore) * 60 + toNumber(report?.minuti);
    totals.oreLdc += toNumber(report?.ore_ldc);
    totals.studi += toNumber(report?.studi);

    parsePublications(report?.pubblicazioni).forEach((publication) => {
      const copies = toNumber(publication?.copie);
      if (copies <= 0) return;

      const title = String(publication?.titolo || "Pubblicazione").trim() || "Pubblicazione";
      const language = String(publication?.lingua || "").trim();
      const key = `${title.toLocaleLowerCase("it")}|${language.toLocaleLowerCase("it")}`;
      const current = publicationMap.get(key) || { key, title, language, copies: 0 };
      current.copies += copies;
      publicationMap.set(key, current);
      totals.publications += copies;
    });
  });

  totals.volunteerCount = volunteerIds.size;
  totals.publicationBreakdown = Array.from(publicationMap.values()).sort(
    (a, b) =>
      b.copies - a.copies ||
      a.title.localeCompare(b.title, "it", { sensitivity: "base" })
  );
  return totals;
};

export const formatServiceDuration = (totalMinutes) => {
  const minutes = Math.max(0, Math.round(toNumber(totalMinutes)));
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};
